import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { PlayerRequirementDialog, PlayerRequirementNotice } from '@/components/ui/SportFormDialog';
import { impactLight } from '@/utils/nativeHaptics';
import {
  applyRotationFormat,
  applyScoringKind,
  buildGeneratePayload,
  createDefaultTambahPadelGameForm,
  isTambahPadelGameSubmitEnabled,
  type PadelRotationFormat,
  type PadelScoringKind,
  type TambahPadelGameFormState,
} from './padelGenerateUtils';
import TambahPadelGameScoringFields from './TambahPadelGameScoringFields';
import TambahPadelGameFormatFields from './TambahPadelGameFormatFields';
import TambahPadelGameTeamsSection, {
  type TambahPadelGamePlayerOption,
} from './TambahPadelGameTeamsSection';

interface TambahPadelGameFormProps {
  confirmedCount: number;
  participants: TambahPadelGamePlayerOption[];
  submitting: boolean;
  onSubmit: (form: TambahPadelGameFormState) => void;
  onFormChange?: (form: TambahPadelGameFormState) => void;
  onInsufficientClose?: () => void;
}

export default function TambahPadelGameForm({
  confirmedCount,
  participants,
  submitting,
  onSubmit,
  onFormChange,
  onInsufficientClose,
}: TambahPadelGameFormProps) {
  const [form, setForm] = useState<TambahPadelGameFormState>(createDefaultTambahPadelGameForm);
  const [picker, setPicker] = useState<{ teamIndex: number; slotIndex: number } | null>(null);

  const enoughPlayers = confirmedCount >= 4;
  const enabled = useMemo(
    () => isTambahPadelGameSubmitEnabled(form, confirmedCount) && !submitting,
    [form, confirmedCount, submitting]
  );
  const playerNotice =
    confirmedCount < 1
      ? 'Belum ada peserta confirmed. Daftarkan pemain di event ini sebelum menambah game.'
      : !enoughPlayers
        ? 'Minimal 4 peserta confirmed diperlukan untuk game ganda.'
        : '';

  const patch = (next: Partial<TambahPadelGameFormState>) => {
    setForm((prev) => {
      let merged = { ...prev, ...next };
      if (next.scoringKind) {
        merged = applyScoringKind(merged, next.scoringKind as PadelScoringKind);
      }
      if (next.rotationFormat) {
        merged = applyRotationFormat(merged, next.rotationFormat as PadelRotationFormat);
      }
      onFormChange?.(merged);
      return merged;
    });
  };

  if (confirmedCount < 1) {
    return (
      <PlayerRequirementDialog
        open
        title="Belum ada pemain terdaftar"
        message={playerNotice}
        onClose={() => onInsufficientClose?.()}
      />
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!enabled) return;
        impactLight();
        buildGeneratePayload(form);
        onSubmit(form);
      }}
    >
      <div className="space-y-4 pb-28">
        {playerNotice ? <PlayerRequirementNotice title="Pemain belum cukup" message={playerNotice} /> : null}
        <TambahPadelGameScoringFields form={form} onChange={patch} />
        <TambahPadelGameFormatFields
          form={form}
          confirmedCount={confirmedCount}
          onChange={patch}
        />
        {form.rotationFormat === 'round_robin' && (
          <TambahPadelGameTeamsSection
            teams={form.teams}
            participants={participants}
            picker={picker}
            onPickerChange={setPicker}
            onTeamsChange={(teams) => patch({ teams })}
          />
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-100 bg-white p-4 pb-safe">
        <button
          type="submit"
          disabled={!enabled}
          className={`h-12 w-full rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-all ${
            enabled
              ? 'bg-[#FC480D] text-white hover:bg-[#D83B08] active:scale-[0.99] shadow-sm cursor-pointer'
              : 'bg-[#EDF0F5] text-gray-400 cursor-not-allowed'
          }`}
        >
          <Icon icon="mdi:shuffle-variant" className="h-4 w-4" />
          Buat Jadwal
        </button>
      </div>
    </form>
  );
}
